import { createSelector } from '@reduxjs/toolkit';

import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { MedicalImageInterface } from '@/shared/config';
import { getFileSrc } from '@/shared/lib';

import {
  assetsModel,
  getGeneratedReportOrder,
  getMedicalImageGroupName,
  getMedicalImageInfo,
  getMedicalImageKind,
  makeAnnotations,
  makeGroupByOrientation,
  makeImageViewOptions,
  makePathologyGroup,
  TOOTH_PAGE_GROUPS,
  TOOTH_PAGE_PATHOLOGY_GROUPS,
} from '@/entities/assets';
import { conditionModel } from '@/entities/condition';
import { toothModel } from '@/entities/tooth';

import { periapicalRadiolucencySpecialGroupsModel } from '@/features/periapicalRadiolucencySpecialGroups';

export const selectActiveMedicalImages = (toothID: string) =>
  createSelector(
    [
      assetsModel.selectors.selectEntities,
      assetsModel.selectors.makeSelectorUserUploadedAssetsByToothID(toothID),
      toothModel.selectors.selectDisplaySlicesByToothID(toothID),
    ],
    (assets, userAssets, displaySlices): MedicalImageInterface[] => {
      const userAssetsIDs = userAssets.map((asset) => asset.ID);

      const result =
        displaySlices?.reduce((acc, selectedMedicalImageID) => {
          const currentMedicalImage = assets[selectedMedicalImageID];

          const { medicalImageID, medicalImagePath } =
            getMedicalImageInfo(currentMedicalImage);

          const currentImageID = currentMedicalImage?.ID ?? '';

          const generatedReportAsset =
            currentMedicalImage?.Kind.case === 'GeneratedReport'
              ? currentMedicalImage.Kind.value
              : undefined;

          const reportAsset =
            currentMedicalImage?.Kind.case === 'Report'
              ? currentMedicalImage.Kind.value
              : undefined;

          const generatedReportOrder =
            getGeneratedReportOrder(generatedReportAsset);

          const order =
            generatedReportOrder ?? userAssetsIDs.indexOf(currentImageID);

          const annotations = makeAnnotations(
            generatedReportAsset?.MedicalImageFeatures?.Annotations?.Layers ??
              reportAsset?.MedicalImageFeatures?.Annotations?.Layers,
          );

          const viewOptions = makeImageViewOptions(
            generatedReportAsset?.MedicalImageFeatures?.ViewOptionsV2 ??
              reportAsset?.MedicalImageFeatures?.ViewOptionsV2,
          );

          if (medicalImageID && currentMedicalImage) {
            acc.push({
              src: getFileSrc(medicalImageID),
              kind: getMedicalImageKind(reportAsset?.Content),
              id: selectedMedicalImageID,
              path: medicalImagePath,
              assetID: currentImageID,
              groupName: getMedicalImageGroupName(currentMedicalImage),
              annotations,
              viewOptions,
              order,
            });
          }

          return acc;
        }, [] as MedicalImageInterface[]) ?? [];

      return result.sort((a, b) => a?.order ?? 0 - (b?.order ?? 0));
    },
  );

export const selectSelectedUploadedMedicalImages = (toothID: string) =>
  createSelector(
    [
      selectActiveMedicalImages(toothID),
      assetsModel.selectors.makeSelectorUserUploadedAssetsByToothID(toothID),
    ],
    (selectedImages, uploadedAssets): MedicalImageInterface[] =>
      uploadedAssets.flatMap((asset) => {
        const image = selectedImages.find(
          (selected) => selected.assetID === asset.ID,
        );

        return image ? [image] : [];
      }),
  );

export const selectMedicalImageGroups = (toothID: string) =>
  createSelector(
    [
      assetsModel.selectors.selectByToothID(toothID),
      conditionModel.selectors.selectSlicesMasks(toothID, [
        ConditionCode.PeriapicalRadiolucency,
      ]),
      periapicalRadiolucencySpecialGroupsModel.selectors.selectPeriapicalRadiolucencyWithAssetsAndLocalizationsByToothID(
        toothID,
      ),
    ],
    (toothAssets, allSlicesMasks, periapicalConditionsData) => {
      const periapicalLesionCode = 1;

      const periapicalGroups = [];

      if (periapicalConditionsData.length > 0) {
        const periapicalGroupConfig = TOOTH_PAGE_PATHOLOGY_GROUPS.find(
          (group) => group.groupCode === periapicalLesionCode,
        );

        if (periapicalGroupConfig) {
          const conditionsByRoot = periapicalConditionsData.reduce(
            (accumulator, conditionData) => {
              const rootType =
                conditionData.condition.Property?.Content.case ===
                'PeriapicalRadiolucency'
                  ? conditionData.condition.Property.Content.value
                      .AffectedRootType
                  : 0;

              if (!accumulator[rootType]) {
                accumulator[rootType] = [];
              }

              return {
                ...accumulator,
                [rootType]: [...accumulator[rootType], conditionData],
              };
            },
            {} as Record<number, typeof periapicalConditionsData>,
          );

          const rootGroups = Object.entries(conditionsByRoot).map(
            ([rootTypeStr, conditions]) => {
              const rootType = Number(rootTypeStr);
              const totalVolume = conditions.reduce((sum, conditionData) => {
                const volume =
                  conditionData.condition.Property?.Content.case ===
                  'PeriapicalRadiolucency'
                    ? conditionData.condition.Property.Content.value
                        .LesionVolume
                    : 0;
                return sum + volume;
              }, 0);

              const assets = conditions.flatMap(
                (conditionData) => conditionData.assets,
              );
              const localizations = conditions.flatMap(
                (conditionData) => conditionData.localizations,
              );

              return {
                rootType,
                volume: totalVolume,
                assets,
                localizations,
              };
            },
          );

          const allAssets = periapicalConditionsData.flatMap(
            (conditionData) => conditionData.assets,
          );
          const allAssetIDs = [...new Set(allAssets.map((asset) => asset.ID))];
          const allLocalizations = periapicalConditionsData.flatMap(
            (conditionData) => conditionData.localizations,
          );

          const periapicalAssets = toothAssets.filter((asset) =>
            allAssetIDs.includes(asset.ID),
          );

          const group = makePathologyGroup(
            periapicalGroupConfig,
            periapicalAssets,
            allLocalizations,
          );

          const groupWithRootInfo = {
            ...group,
            rootGroups,
          };

          periapicalGroups.push(groupWithRootInfo);
        }
      }

      const otherPathologyGroups = TOOTH_PAGE_PATHOLOGY_GROUPS.filter(
        (group) => group.groupCode !== periapicalLesionCode,
      ).map((group) => makePathologyGroup(group, toothAssets, allSlicesMasks));

      const groups = TOOTH_PAGE_GROUPS.map((group) =>
        makeGroupByOrientation(group, toothAssets),
      );

      return [...groups, ...otherPathologyGroups, ...periapicalGroups];
    },
  );
